import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pendencias } from '../Pendencias';
import { ErroApi } from '../api';
import { gravarClassificacao, verClassificacao } from '../api/classificacao';
import type {
  CandidataGranulometria, CandidataLimite, EnsaioClassificacao,
} from '../api/classificacao';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EtapasDaFicha,
  FaixaDaFicha, LaudoDaFicha, RodapeDeEtapas, fichaCongelada, useEtapa,
  useFicha,
} from '../Ficha';
import type { Etapa, Indicador } from '../Ficha';
import { data, numero, paraNumero } from '../formato';
import { Achado, Plano, Segmento } from '../Grafico';
import {
  Aviso, Campo, Carregando, ErroDaApi, Escolha, RespostaDaAcao, Selo,
} from '../ui';
import type { Opcao } from '../ui';

/**
 * A classificação SUCS e HRB — onda 217, migração `0237`, RN-205.
 *
 * «É um item medido e cobrado à parte» (Humberto, 28/09/2026); «SUCS pela
 * ASTM D2487 e HRB pela AASHTO M 145», «mantenha AASHTO e tire DNIT» (o
 * índice de grupo) e «mantenha o padrão novo de ficha de ensaio»
 * (04/10/2026).
 *
 * ─── A espinha de sempre (236) ─────────────────────────────────────────
 *
 * Cabeçalho, faixa com os números do servidor, etapas na URL, blocos,
 * rodapé. Três etapas:
 *
 *   1. FONTES — a granulometria e os limites APROVADOS da mesma amostra,
 *      com o mais novo sugerido; o LL seco em estufa (critério de
 *      orgânico) e as observações;
 *   2. CLASSIFICAÇÃO — a SUCS e a HRB com o caminho da decisão por
 *      extenso (o MESMO texto do laudo, que vem do banco) e a carta de
 *      plasticidade;
 *   3. LAUDO.
 *
 * A tela não classifica nada: tudo vem da `vw_ensaio_classificacao`.
 */

const ORIGEM: Record<string, string> = {
  interpolada: 'interpolada na curva',
  acima_da_maior: 'acima da maior peneira',
  fora_da_curva: 'fora da curva',
  sem_curva: 'sem curva',
};

const num = (v: unknown, casas: number) => numero(v, casas);

export function Classificacao() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioClassificacao | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ficha = useFicha(id, 'D2487', versao);

  const carregar = useCallback(async () => {
    try { setD(await verClassificacao(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const c = d?.classificacao ?? null;
  const fechou = c?.situacao === 'ok';
  const [etapa, irPara] = useEtapa(3, !c ? 1 : 2);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo a classificação" />;
  const congelado = fichaCongelada(ficha?.status);

  const gravar = async (corpo: unknown) => {
    setSalvando(true); setErro(null); setOk(null);
    try {
      setD(await gravarClassificacao(id, corpo));
      setOk('Gravado.');
      setVersao((v) => v + 1);
      irPara(2);
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  };

  const etapas: Etapa[] = [
    { nome: 'Fontes', feita: c != null,
      resumo: c ? 'granulometria e limites escolhidos' : 'a escolher' },
    { nome: 'Classificação', feita: fechou, falta: c != null && !fechou,
      resumo: fechou ? `${c.sucs_simbolo} · ${c.hrb_grupo}` : 'a classificar' },
    { nome: 'Laudo', feita: ficha?.status === 'aprovado' },
  ];

  const porque = c ? (c.situacao !== 'ok' ? 'A classificação não fechou — veja o que falta.' : undefined)
    : 'Escolha as fontes na etapa 1.';
  const indicadores: Indicador[] = [
    { rotulo: 'SUCS', chave: true,
      valor: c?.sucs_simbolo ?? null, nota: c?.sucs_nome ?? undefined, porque,
      como: <>ASTM D2487-17(2025): carta de plasticidade (linha A IP =
        0,73·(LL − 20)) para o solo fino; Cu, Cc e os finos para o grosso;
        nome do grupo pelas Figuras 1 e 3.</> },
    { rotulo: 'HRB', chave: true,
      valor: c?.hrb_grupo ? `${c.hrb_grupo} (${num(c.ig_inteiro, 0)})` : null,
      nota: c?.hrb_grupo ? 'grupo (índice de grupo)' : undefined, porque,
      como: <>AASHTO M 145, Tabela 2, eliminação da esquerda para a direita;
        IG = (F − 35)·[0,2 + 0,005·(LL − 40)] + 0,01·(F − 15)·(IP − 10),
        sem limite superior, inteiro.</> },
    { rotulo: 'Finos · nº 200', unidade: '%',
      valor: c?.finos != null ? num(c.finos, 1) : null, porque,
      nota: c?.origem_200 && ORIGEM[c.origem_200] ? ORIGEM[c.origem_200] : undefined },
    { rotulo: 'LL · IP', unidade: '%',
      valor: c ? (c.np ? 'NP'
        : c.ll != null && c.ip != null ? `${num(c.ll, 0)} · ${num(c.ip, 0)}` : null) : null,
      porque },
  ];

  return (
    <div className="tela classificacao ficha-carbon">
      <CabecalhoDaFicha id={id} sigla="CLASS" titulo="Classificação SUCS e HRB"
        ficha={ficha} normaDaTela="ASTM D2487 · AASHTO M 145" />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />

      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${String(ficha?.status ?? '').replace(/_/g, ' ')} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Classificação aprovada não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo.'}
        </Aviso>
      )}

      <fieldset className="so-leitura" disabled={congelado}>
        {etapa === 1 && (
          <Fontes d={d} salvando={salvando} aoGravar={gravar}
            campoErro={(x) => (erro instanceof ErroApi ? erro.campo(x) : undefined)} />
        )}
        {etapa === 2 && (c
          ? <Resultado c={c} />
          : (
            <Aviso tipo="atencao" titulo="Ainda sem fontes">
              Escolha a granulometria e os limites de Atterberg na etapa 1.
            </Aviso>
          ))}
        {etapa === 3 && <LaudoDaFicha id={id} />}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

// ── 1 · as fontes ─────────────────────────────────────────────────────

const quando = (x: { laudo: string | null; metodo_codigo: string; data_execucao: string | null }) =>
  [x.laudo ?? x.metodo_codigo, data(x.data_execucao)].filter(Boolean).join(' · ');

function opcoesGran(lista: CandidataGranulometria[]): Opcao[] {
  return lista.map((g) => ({
    valor: g.id,
    texto: `${quando(g)} · ${g.passa_200 != null ? `${num(g.passa_200, 1)} % na nº 200` : 'sem nº 200'}`
      + (g.com_sedimentacao ? ' · com sedimentação' : ''),
  }));
}

function opcoesLim(lista: CandidataLimite[], qual: 'll' | 'lp'): Opcao[] {
  return lista.map((l) => {
    const v = qual === 'll'
      ? (l.nao_liquido ? 'NL' : l.limite_liquidez != null ? `LL ${num(l.limite_liquidez, 1)} %` : 'sem LL')
      : (l.nao_plastico ? 'NP' : l.limite_plasticidade != null ? `LP ${num(l.limite_plasticidade, 1)} %` : 'sem LP');
    return { valor: l.id, texto: `${quando(l)} · ${v}` };
  });
}

function Fontes({ d, salvando, aoGravar, campoErro }: {
  d: EnsaioClassificacao; salvando: boolean;
  aoGravar(corpo: unknown): Promise<void>;
  campoErro(x: string): string | undefined;
}) {
  const c = d.classificacao;
  const { granulometrias, liquidez, plasticidade } = d.candidatas;
  /* A sugestão é a fonte já gravada, ou a mais nova aprovada. */
  const [gran, setGran] = useState<string | null>(null);
  const [ll, setLl] = useState<string | null>(null);
  const [lp, setLp] = useState<string | null>(null);
  const [seco, setSeco] = useState('');
  const [obs, setObs] = useState('');

  useEffect(() => {
    setGran(c?.granulometria_ensaio_id ?? granulometrias[0]?.id ?? null);
    const llId = c?.limite_liquidez_ensaio_id ?? liquidez[0]?.id ?? null;
    setLl(llId);
    /* O LA tem as duas fichas no mesmo ensaio: o LP sugerido é o do
       mesmo ensaio do LL, quando ele existe. */
    setLp(c?.limite_plasticidade_ensaio_id
      ?? plasticidade.find((p) => p.id === llId)?.id
      ?? plasticidade[0]?.id ?? null);
    setSeco(c?.ll_seco_estufa != null ? String(c.ll_seco_estufa) : '');
    setObs(c?.observacoes ?? '');
  }, [c?.granulometria_ensaio_id, c?.limite_liquidez_ensaio_id,
    c?.limite_plasticidade_ensaio_id, c?.ll_seco_estufa, c?.observacoes,
    granulometrias, liquidez, plasticidade]);

  const falta = !gran ? 'Escolha a granulometria.'
    : !ll ? 'Escolha o ensaio do limite de liquidez.'
      : !lp ? 'Escolha o ensaio do limite de plasticidade.' : null;

  const enviar = () => {
    if (falta || salvando) return;
    void aoGravar({
      granulometriaEnsaioId: gran,
      limiteLiquidezEnsaioId: ll,
      limitePlasticidadeEnsaioId: lp,
      llSecoEstufa: paraNumero(seco) ?? null,
      observacoes: obs.trim() || null,
    });
  };

  const semFonte = (o: string) => (
    <Aviso tipo="atencao" titulo={`A amostra não tem ${o} aprovada`}>
      A classificação lê só resultado aprovado da mesma amostra (RN-205):
      aprove {o === 'granulometria' ? 'a granulometria' : 'os limites de Atterberg'} primeiro.
    </Aviso>
  );

  return (
    <BlocoDaFicha titulo="Fontes da classificação"
      selo={c ? <Selo tom="ok">Gravadas</Selo> : undefined}
      sub="Os resultados aprovados da mesma amostra. O mais novo vem sugerido; quem classifica confere e grava.">
      <div className="fe-miolo fe-campos fe-fontes">
        {granulometrias.length
          ? <Escolha rotulo="Granulometria" valor={gran} aoEscolher={setGran}
              limpavel={false} opcoes={opcoesGran(granulometrias)}
              erro={campoErro('granulometriaEnsaioId')} />
          : semFonte('granulometria')}
        {liquidez.length
          ? <Escolha rotulo="Limite de liquidez" valor={ll} aoEscolher={setLl}
              limpavel={false} opcoes={opcoesLim(liquidez, 'll')}
              erro={campoErro('limiteLiquidezEnsaioId')} />
          : semFonte('limite de liquidez')}
        {plasticidade.length
          ? <Escolha rotulo="Limite de plasticidade" valor={lp} aoEscolher={setLp}
              limpavel={false} opcoes={opcoesLim(plasticidade, 'lp')}
              erro={campoErro('limitePlasticidadeEnsaioId')} />
          : semFonte('limite de plasticidade')}
        <Campo rotulo="LL de porção seca em estufa (%)" inputMode="decimal"
          value={seco} onChange={(e) => setSeco(e.target.value)}
          erro={campoErro('llSecoEstufa')}
          dica="Opcional. ASTM D2487: LL seco / LL < 0,75 → solo orgânico (OL/OH). Em branco, o solo é classificado como inorgânico." />
        <Campo rotulo="Observações" value={obs}
          onChange={(e) => setObs(e.target.value)} />
      </div>
      <div className="fe-acoes-bloco">
        {falta && <p className="o-que-falta">{falta}</p>}
        <button type="button" className="principal"
          aria-disabled={falta !== null || salvando}
          title={falta ?? undefined} onClick={enviar}>
          {salvando ? 'Classificando…' : 'Classificar com estes resultados'}
        </button>
      </div>
    </BlocoDaFicha>
  );
}

// ── 2 · o resultado ───────────────────────────────────────────────────

function Resultado({ c }: { c: any }) {
  return (
    <>
      <BlocoDaFicha titulo="Dados de entrada"
        sub="Do material que passa em 75 mm (3″). LL e LP inteiros, como a norma os escreve.">
        <div className="fe-tabela">
          <table className="tabela fe-entrada">
            <tbody>
              {[
                ['Passa em 75 mm (3″)', c.passa_3pol_bruto, c.origem_3pol],
                ['Passa na nº 4 (4,8 mm)', c.passa_4, c.origem_4],
                ['Passa na nº 10 (2,0 mm)', c.passa_10, c.origem_10],
                ['Passa na nº 40 (0,42 mm)', c.passa_40, c.origem_40],
                ['Passa na nº 200 (0,075 mm)', c.passa_200, c.origem_200],
              ].map(([r, v, o]) => (
                <tr key={r as string}>
                  <th scope="row">{r as string}</th>
                  <td className="num">{num(v, 1)} %</td>
                  <td className="sub">{ORIGEM[o as string] ?? ''}</td>
                </tr>
              ))}
              <tr><th scope="row">Pedregulho · areia · finos</th>
                <td className="num">{num(c.pedregulho, 1)} · {num(c.areia, 1)} · {num(c.finos, 1)} %</td>
                <td /></tr>
              <tr><th scope="row">D₁₀ · D₃₀ · D₆₀</th>
                <td className="num">{num(c.d10, 4)} · {num(c.d30, 4)} · {num(c.d60, 4)} mm</td>
                <td className="sub">interpolados na curva</td></tr>
              <tr><th scope="row">Cu · Cc</th>
                <td className="num">{num(c.cu, 1)} · {num(c.cc, 2)}</td><td /></tr>
              <tr><th scope="row">LL · LP · IP</th>
                <td className="num">{c.nao_liquido ? 'NL' : num(c.ll, 0)} · {c.nao_plastico ? 'NP' : num(c.lp, 0)} · {c.np ? 'NP' : num(c.ip, 0)}</td>
                <td className="sub">{c.ll_seco_estufa != null
                  ? `LL seco ${num(c.ll_seco_estufa, 1)} % → razão ${num(c.razao_ll_seco, 2)} (${c.organico ? 'orgânico' : 'inorgânico'})`
                  : 'LL seco em estufa não informado — inorgânico'}</td></tr>
            </tbody>
          </table>
        </div>
      </BlocoDaFicha>

      <BlocoDaFicha titulo="SUCS — ASTM D2487-17(2025)"
        selo={c.sucs_simbolo
          ? <Selo tom="ok">{c.sucs_simbolo}</Selo>
          : <Selo tom="critico">Não fechou</Selo>}>
        <div className="fe-miolo">
          <p className="fe-classe">
            <b className="fe-classe-simbolo">{c.sucs_simbolo ?? '—'}</b>
            <span>{c.sucs_nome ?? 'A classificação não fechou: veja o que falta.'}
              {c.sucs_nome_astm && <small lang="en">{c.sucs_nome_astm}</small>}
            </span>
          </p>
          {c.sucs_criterio && <p className="fe-criterio">{c.sucs_criterio}</p>}
          {!c.np && c.ll != null && c.ip != null && (
            <CartaDePlasticidade ll={Number(c.ll)} ip={Number(c.ip)} />
          )}
        </div>
      </BlocoDaFicha>

      <BlocoDaFicha titulo="HRB — AASHTO M 145"
        selo={c.hrb_grupo
          ? <Selo tom="ok">{`${c.hrb_grupo} (${num(c.ig_inteiro, 0)})`}</Selo>
          : <Selo tom="critico">Não fechou</Selo>}>
        <div className="fe-miolo">
          <p className="fe-classe">
            <b className="fe-classe-simbolo">{c.hrb_grupo ?? '—'}</b>
            <span>Índice de grupo {num(c.ig, 2)} → {num(c.ig_inteiro, 0)}
              <small>IG = (F − 35)·[0,2 + 0,005·(LL − 40)] + 0,01·(F − 15)·(IP − 10)
                = {num(c.ig_parcela_ll, 2)} + {num(c.ig_parcela_ip, 2)}</small>
            </span>
          </p>
          {c.hrb_criterio && <p className="fe-criterio">{c.hrb_criterio}</p>}
        </div>
      </BlocoDaFicha>
    </>
  );
}

/**
 * A carta de plasticidade da D2487 (Figura 4): linha A com o patamar em
 * IP = 4, linha U, a faixa CL-ML entre IP 4 e 7 e a divisória LL = 50.
 */
function CartaDePlasticidade({ ll, ip }: { ll: number; ip: number }) {
  const janela = useMemo(() => {
    const x1 = Math.max(100, Math.ceil((ll + 10) / 10) * 10);
    return { x0: 0, x1, y0: 0, y1: Math.max(60, Math.ceil((ip + 10) / 10) * 10) };
  }, [ll, ip]);
  const aIni = 20 + 4 / 0.73;
  return (
    <figure className="fe-grafico">
      <Plano janela={janela} tituloX="Limite de liquidez, LL (%)"
        tituloY="Índice de plasticidade, IP (%)">
        {({ px }) => (
          <>
            <Segmento a={{ x: aIni, y: 4 }}
              b={{ x: janela.x1, y: 0.73 * (janela.x1 - 20) }} px={px} />
            <Segmento a={{ x: 16, y: 0 }} b={{ x: 16, y: 7.2 }} px={px}
              classe="reta auxiliar" />
            <Segmento a={{ x: 16, y: 7.2 }}
              b={{ x: janela.x1, y: 0.9 * (janela.x1 - 8) }} px={px}
              classe="reta auxiliar" />
            <Segmento a={{ x: 8 + 4 / 0.9, y: 4 }} b={{ x: aIni, y: 4 }} px={px}
              classe="reta auxiliar" />
            <Segmento a={{ x: 8 + 7 / 0.9, y: 7 }} b={{ x: 20 + 7 / 0.73, y: 7 }}
              px={px} classe="reta auxiliar" />
            <Segmento a={{ x: 50, y: 0 }} b={{ x: 50, y: janela.y1 }} px={px}
              classe="reta auxiliar" />
            <Achado p={{ x: ll, y: ip }} px={px} rotulo={`LL ${ll} · IP ${ip}`} />
          </>
        )}
      </Plano>
      <figcaption className="sub">
        Linha A (cheia): IP = 0,73·(LL − 20). Linha U (tracejada): IP =
        0,9·(LL − 8). Faixa CL-ML entre IP 4 e 7; divisória em LL = 50.
      </figcaption>
    </figure>
  );
}
